import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { authRedirectUrl, friendlyAuthError } from "@/lib/auth";
import { AuthShell, ErrorMessage } from "@/components/auth/AuthShell";

export const Route = createFileRoute("/verify-email")({
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { email } = Route.useSearch();
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  async function resend() {
    if (!email) return;
    setError(null);
    setState("sending");
    const { error: resendError } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: authRedirectUrl() },
    });
    if (resendError) {
      setError(friendlyAuthError(resendError.message));
      setState("idle");
    } else {
      setState("sent");
    }
  }

  return (
    <AuthShell title="Check your email" subtitle="One more step to activate your account.">
      <p className="text-sm leading-relaxed text-white/80">
        We sent a confirmation link to {email ? <strong className="text-white">{email}</strong> : "your email"}. Open it on
        this device to finish creating your account. If you don't see it, check your spam folder.
      </p>
      <div className="mt-6 space-y-3">
        <ErrorMessage message={error} />
        {email && (
          <button
            type="button"
            onClick={resend}
            disabled={state !== "idle"}
            className="h-11 w-full rounded-md border border-white/20 text-sm font-semibold hover:border-white/40 disabled:opacity-60"
          >
            {state === "sent" ? "Email sent again" : state === "sending" ? "Sending…" : "Resend confirmation email"}
          </button>
        )}
        <p className="text-center text-sm text-white/60">
          Already confirmed?{" "}
          <Link to="/login" className="font-semibold text-white underline decoration-a1-red underline-offset-4">
            Sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
